import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { registerPasskey, isPlatformAuthenticatorAvailable, isPasskeySupported } from '../services/passkeyService';

/**
 * UpgradeToPasskey Component
 * Shown when users click "Forgot Password" or "Change Password"
 * Encourages them to switch to passkey instead
 */
export const UpgradeToPasskey = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const [hasPlatformAuth, setHasPlatformAuth] = useState(false);
  const reason = searchParams.get('reason'); // 'forgot' or 'change'

  useEffect(() => {
    checkSupport();
  }, []);

  const checkSupport = async () => {
    const supported = isPasskeySupported();
    const platformAuth = await isPlatformAuthenticatorAvailable();
    setIsSupported(supported);
    setHasPlatformAuth(platformAuth);
  };

  const handleUpgrade = async () => {
    try {
      setLoading(true);
      setError(null);

      await registerPasskey(deviceName || null);

      setSuccess(true);
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
    } catch (err) {
      console.error('Passkey upgrade error:', err);
      setError(err.message || 'Failed to set up passkey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (reason === 'forgot') {
      // Redirect to traditional forgot password (if you implement it)
      navigate('/login');
    } else {
      // Go back to profile
      navigate('/profile');
    }
  };

  if (!isSupported) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <div className="text-red-600 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-brown-800 mb-4">Browser Not Supported</h2>
          <p className="text-brown-600 mb-4">
            Your browser doesn't support passkeys. Please use a modern browser or continue with password login.
          </p>
          <button
            onClick={handleSkip}
            className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded-full"
          >
            Continue with Password
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <div className="text-green-600 text-4xl mb-4">✓</div>
          <h2 className="text-xl font-bold text-green-800 mb-4">You're All Set!</h2>
          <p className="text-brown-600 mb-4">
            You can now sign in with {hasPlatformAuth ? 'Face ID/Touch ID' : 'your passkey'} instead of a password.
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-green-700">
              ✓ No more passwords to remember<br/>
              ✓ Faster and more secure login<br/>
              ✓ Works across your devices
            </p>
          </div>
          <p className="text-sm text-brown-500 mt-4">Redirecting to your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
      <div className="text-center mb-6">
        <div className="text-4xl mb-4">🔐</div>
        <h2 className="text-2xl font-bold text-brown-800 mb-2">
          {reason === 'forgot' ? 'Forgot Your Password?' : 'No More Passwords Needed!'}
        </h2>
        <p className="text-lg text-brown-600 mb-4">
          {reason === 'forgot'
            ? 'Instead of resetting your password, switch to Face ID/Touch ID for instant, secure login.'
            : 'Switch to biometric authentication and never worry about changing passwords again.'}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <div className="text-3xl mb-2">⚡</div>
          <h3 className="font-bold text-blue-800 mb-1">Faster</h3>
          <p className="text-sm text-blue-700">Sign in with just a tap or glance</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <div className="text-3xl mb-2">🔒</div>
          <h3 className="font-bold text-green-800 mb-1">More Secure</h3>
          <p className="text-sm text-green-700">No password to steal or forget</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
          <div className="text-3xl mb-2">☁️</div>
          <h3 className="font-bold text-purple-800 mb-1">Syncs</h3>
          <p className="text-sm text-purple-700">Works across all your devices</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="mb-6">
        <label htmlFor="deviceName" className="block text-brown-700 text-sm font-bold mb-2">
          Device Name (Optional)
        </label>
        <input
          type="text"
          id="deviceName"
          value={deviceName}
          onChange={(e) => setDeviceName(e.target.value)}
          placeholder={hasPlatformAuth ? 'e.g., My iPhone' : 'e.g., My Device'}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-brown-700 leading-tight focus:outline-none focus:shadow-outline"
          disabled={loading}
        />
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <h3 className="font-bold text-yellow-800 mb-2">How it works:</h3>
        <ol className="text-sm text-yellow-700 space-y-1 list-decimal list-inside">
          <li>Click "Switch to {hasPlatformAuth ? 'Face ID/Touch ID' : 'Passkey'}" below</li>
          <li>{hasPlatformAuth ? 'Verify with your face or fingerprint' : 'Follow your browser prompts'}</li>
          <li>Next time, sign in instantly - no password needed!</li>
        </ol>
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleSkip}
          className="flex-1 bg-gray-300 hover:bg-gray-400 text-brown-800 font-bold py-3 px-4 rounded-full"
          disabled={loading}
        >
          {reason === 'forgot' ? 'Use Password Login' : 'Skip for Now'}
        </button>
        <button
          onClick={handleUpgrade}
          disabled={loading}
          className="flex-1 bg-brown-600 hover:bg-brown-700 text-white font-bold py-3 px-4 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Setting Up...' : `Switch to ${hasPlatformAuth ? 'Face ID/Touch ID' : 'Passkey'}`}
        </button>
      </div>

      <div className="mt-6 text-center">
        <p className="text-xs text-brown-500">
          Your biometric data stays on your device and is never shared with anyone.
        </p>
      </div>
    </div>
  );
};

export default UpgradeToPasskey;
